import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Calculator, Upload, GripVertical, Trash2, CreditCard } from 'lucide-react';
import CustomerSelectorField from './CustomerSelectorField';
import SalespersonField from './SalespersonField';
import {
  createAccountInvoice,
  updateAccountInvoice,
  getAccountInvoiceById,
  getAccountInvoiceItems,
  createAccountInvoiceItem,
  deleteAccountInvoiceItem,
  uploadMenuFiles,
  deleteMenuFile,
  getCustomerCreditNotes,
  getAccountSalesOrders,
  getVesselList,
} from '../core/_requests';
import { useAccountCustomers } from '../core/useAccountCustomers';
import { setupAxios } from '../../auth';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

type UploadedFile = {
  originalName: string;
  fileName: string;
  size: number;
  mimeType?: string;
  path: string;
  url?: string;
};

export default function AccountsNewInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const invoiceId = id ? Number(id) : null;
  const isEditing = invoiceId !== null && !Number.isNaN(invoiceId);
  const [saving, setSaving] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const customersHook = useAccountCustomers();
  const { customers } = customersHook;
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();

  // Form State
  const [vessels, setVessels] = useState<any[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [selectedVesselName, setSelectedVesselName] = useState('');
  const [selectedAccountCode, setSelectedAccountCode] = useState('');
  const [availableAccountCodes, setAvailableAccountCodes] = useState<string[]>([]);
  const [loadingAccountCodes, setLoadingAccountCodes] = useState(false);

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [allSalesOrders, setAllSalesOrders] = useState<any[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState(`INV-${String(Date.now()).slice(-5)}`);
  const [orderNumber, setOrderNumber] = useState('');
  const [requisitionNumber, setRequisitionNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Due on Receipt');
  const [salesperson, setSalesperson] = useState('');
  const [subject, setSubject] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('Draft');

  // Line Items
  const [items, setItems] = useState([
    { id: 1, description: '', accountCode: '', quantity: '1.00', rate: '0.00' }
  ]);

  // Totals & Settings
  const [customerNotes, setCustomerNotes] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [taxType, setTaxType] = useState('TDS');
  const [taxRate, setTaxRate] = useState('');
  const [adjustment, setAdjustment] = useState('0');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Credit Notes
  type AppliedCredit = { id: number; creditNoteNumber: string; availableBalance: number; appliedAmount: number };
  const [customerCreditNotes, setCustomerCreditNotes] = useState<any[]>([]);
  const [appliedCredits, setAppliedCredits] = useState<AppliedCredit[]>([]);

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry;
        let url = entry;
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`;
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile;
      });
  };

  // Load vessels
  useEffect(() => {
    if (globalVessels && globalVessels.length > 0) {
      setVessels(globalVessels);
    } else {
      getVesselList()
        .then((data) => {
          if (Array.isArray(data)) setVessels(data);
        })
        .catch((err) => console.error('Failed to load vessels:', err));
    }
  }, [globalVessels]);

  // Default vessel when creating a new invoice if global filter vessel is selected
  useEffect(() => {
    if (!isEditing && !selectedVesselId && globalState.vessel?.id) {
      setSelectedVesselId(String(globalState.vessel.id));
      setSelectedVesselName(globalState.vessel.fleetName || (globalState.vessel as any).fleet_name || '');
    }
  }, [isEditing, globalState.vessel, selectedVesselId]);

  // Load account codes based on selected vessel and company
  const loadAccountCodesForVessel = async (vesselId: string | number) => {
    if (!vesselId) {
      setAvailableAccountCodes([]);
      return;
    }
    setLoadingAccountCodes(true);
    try {
      const params = new URLSearchParams();
      params.append('vesselId', String(vesselId));
      if (globalState.company?.id) {
        params.append('companyId', String(globalState.company.id));
      }
      const response = await api.get(`/accounts/budgets/account-codes?${params.toString()}`);
      const data = response.data?.data || response.data || [];
      const rows = Array.isArray(data) ? data : [];
      const codes = Array.from(
        new Set(
          rows
            .map((r: any) => (r.accountCodes || r.account_codes || r.accountCode || '').trim())
            .filter(Boolean)
        )
      ) as string[];
      setAvailableAccountCodes(codes);
    } catch (err) {
      console.error('Failed to fetch account codes:', err);
      setAvailableAccountCodes([]);
    } finally {
      setLoadingAccountCodes(false);
    }
  };

  useEffect(() => {
    if (selectedVesselId) {
      loadAccountCodesForVessel(selectedVesselId);
    } else {
      setAvailableAccountCodes([]);
    }
  }, [selectedVesselId, globalState.company?.id]);

  const handleVesselChange = (vesselId: string) => {
    setSelectedVesselId(vesselId);
    const vessel = vessels.find((v: any) => String(v.id) === String(vesselId));
    const vName = vessel?.fleetName || vessel?.fleet_name || vessel?.name || '';
    setSelectedVesselName(vName);
    setSelectedAccountCode('');
  };

  // Load existing invoice for edit mode
  useEffect(() => {
    if (!isEditing || invoiceId == null) return;
    let isActive = true;
    setLoadingInvoice(true);
    Promise.allSettled([getAccountInvoiceById(invoiceId), getAccountInvoiceItems(invoiceId)])
      .then(([invoiceRes, itemsRes]) => {
        if (!isActive) return;
        if (invoiceRes.status === 'fulfilled') {
          const data = invoiceRes.value;
          setSelectedCustomerId(data.customerId ? String(data.customerId) : '');
          setSelectedCustomerName(data.customerName || '');
          const vId = data.vesselId || data.vessel_id ? String(data.vesselId || data.vessel_id) : '';
          const vName = data.vesselName || data.vessel_name || '';
          const accCode = data.accountCode || data.account_code || '';
          setSelectedVesselId(vId);
          setSelectedVesselName(vName);
          setSelectedAccountCode(accCode);
          setInvoiceNumber(data.invoiceNumber || `INV-${String(Date.now()).slice(-5)}`);
          setOrderNumber(data.orderNumber || '');
          setRequisitionNumber(data.requisitionNumber || '');
          setInvoiceDate(data.invoiceDate ? new Date(data.invoiceDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
          setDueDate(data.dueDate ? new Date(data.dueDate).toISOString().slice(0, 10) : '');
          setSalesperson(data.salesperson || '');
          setSubject(data.subject || '');
          setInvoiceStatus(data.status || 'Draft');
          setCustomerNotes(data.customerNotes || '');
          setTermsConditions(data.termsConditions || '');
          setDiscountPercent(String(data.discountPercent ?? 0));
          setTaxType(data.taxType || 'TDS');
          const parsedRate = data.taxName?.match(/(\d+(?:\.\d+)?)\s*%/);
          setTaxRate(parsedRate ? parsedRate[1] : '');
          setAdjustment(String(data.adjustment ?? 0));

          const filePathValue = data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? '';
          const existingFiles = buildUploadedFiles(filePathValue);
          setUploadedFiles(existingFiles);

          const rawItems = itemsRes.status === 'fulfilled' && Array.isArray(itemsRes.value) && itemsRes.value.length > 0
            ? itemsRes.value
            : Array.isArray(data.items) ? data.items : [];
          if (rawItems.length > 0) {
            const sorted = [...rawItems].sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
            setItems(sorted.map((item, idx) => ({
              id: Number(item.id ?? idx + 1),
              description: String(item.description || item.itemName || ''),
              accountCode: String(item.accountCode || item.account_code || item.account || ''),
              quantity: String(item.quantity ?? '1.00'),
              rate: String(item.rate ?? '0.00'),
            })));
          }
        }
      })
      .catch(err => console.error('Failed to load invoice:', err))
      .finally(() => { if (isActive) setLoadingInvoice(false); });
    return () => { isActive = false; };
  }, [invoiceId, isEditing]);

  // Fetch credit notes when customer changes
  useEffect(() => {
    setCustomerCreditNotes([]);
    setAppliedCredits([]);
    if (!selectedCustomerId) return;
    const custId = Number(selectedCustomerId);
    if (!custId || isNaN(custId)) return;
    getCustomerCreditNotes(custId)
      .then((data) => setCustomerCreditNotes(Array.isArray(data) ? data : []))
      .catch(() => setCustomerCreditNotes([]));
  }, [selectedCustomerId]);

  // Fetch all sales orders
  useEffect(() => {
    getAccountSalesOrders()
      .then(res => {
        if (Array.isArray(res)) setAllSalesOrders(res);
      })
      .catch(err => console.error('Failed to load sales orders:', err));
  }, []);

  const handleSalesOrderSelect = (orderNumberValue: string) => {
    setOrderNumber(orderNumberValue);
    if (!orderNumberValue) return;
    
    const order = allSalesOrders.find(o => o.orderNumber === orderNumberValue);
    if (!order) return;

    setPaymentTerms(order.paymentTerms || 'Due on Receipt');
    setSalesperson(order.salesperson || '');
    setCustomerNotes(order.customerNotes || '');
    setTermsConditions(order.termsConditions || '');
    setDiscountPercent(String(order.discountPercent ?? 0));
    setTaxType(order.taxType || 'TDS');
    
    const parsedRate = order.taxName?.match(/(\d+(?:\.\d+)?)\s*%/);
    setTaxRate(parsedRate ? parsedRate[1] : '');
    setAdjustment(String(order.adjustment ?? 0));

    if (order.items && Array.isArray(order.items)) {
      const sorted = [...order.items].sort((a: any, b: any) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
      setItems(sorted.map((item: any, idx: number) => ({
        id: Number(item.id ?? idx + 1),
        description: String(item.description || item.itemName || ''),
        accountCode: String(item.accountCode || item.account_code || item.account || ''),
        quantity: String(item.quantity ?? '1.00'),
        rate: String(item.rate ?? '0.00'),
      })));
    }
  };

  // Calculations
  const toNumber = (val) => {
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : 0;
  };

  const getRowAmount = (row) => toNumber(row.quantity) * toNumber(row.rate);
  const subTotal = items.reduce((sum, row) => sum + getRowAmount(row), 0);
  const discountAmount = subTotal * (toNumber(discountPercent) / 100);
  const adjustmentAmount = toNumber(adjustment);
  const taxableBase = Math.max(0, subTotal - discountAmount + adjustmentAmount);

  const activeTaxOptions = taxType === 'TDS' ? [
    { label: 'Dividend [10%]', rate: 10 },
    { label: 'Other Interest [10%]', rate: 10 },
    { label: 'Contractors [2%]', rate: 2 },
  ] : [
    { label: 'TCS on Goods [0.1%]', rate: 0.1 }
  ];

  const selectedTax = activeTaxOptions.find(opt => opt.rate === Number(taxRate));
  const taxAmountRaw = taxableBase * ((selectedTax?.rate || 0) / 100);
  const taxAmount = taxType === 'TDS' ? -taxAmountRaw : taxAmountRaw;
  const total = taxableBase + taxAmount;
  const totalCreditsApplied = appliedCredits.reduce((sum, c) => sum + (c.appliedAmount || 0), 0);
  const finalTotal = Math.max(0, total - totalCreditsApplied);



  const buildLineItemPayload = (row: any, order: number) => ({
    itemOrder: order,
    itemName: row.description.trim() || null,
    description: row.description.trim() || null,
    account: row.accountCode || null,
    accountCode: row.accountCode || null,
    quantity: toNumber(row.quantity),
    rate: toNumber(row.rate),
    amount: getRowAmount(row),
  });

  const syncLineItems = async (targetId: number) => {
    let existing: any[] = [];
    try { existing = await getAccountInvoiceItems(targetId); } catch (err) { console.warn('Failed to load existing items:', err); }
    if (existing.length > 0) {
      await Promise.all(existing.map((item) => deleteAccountInvoiceItem(targetId, item.id)));
    }
    const cleaned = items
      .filter(row => row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1)
      .map((row, index) => buildLineItemPayload(row, index + 1));
    if (cleaned.length > 0) {
      await Promise.all(cleaned.map(item => createAccountInvoiceItem(targetId, item)));
    }
  };

  const handleSave = async (statusOverride = 'Draft') => {
    setSaving(true);
    try {
      const customer = customers.find(c => String(c.id) === selectedCustomerId);
      const customerName = customer?.displayName || customer?.name || customer?.companyName || selectedCustomerName || '';
      const customerId = selectedCustomerId ? Number(selectedCustomerId) : null;
      const invoiceDateIso = invoiceDate || new Date().toISOString().slice(0, 10);
      const dueDateIso = dueDate || null;
      const status = typeof statusOverride === 'string' ? statusOverride : invoiceStatus || 'Draft';
      const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean);

      const cleanedItems = items
        .filter(row => row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1)
        .map((row, index) => buildLineItemPayload(row, index + 1));

      const matchedVessel = vessels.find((v: any) => String(v.id) === String(selectedVesselId));
      const vName = selectedVesselName || matchedVessel?.fleetName || matchedVessel?.fleet_name || matchedVessel?.name || null;
      const distinctAccountCodes = Array.from(
        new Set(cleanedItems.map((item) => item.accountCode).filter(Boolean))
      ).join(', ');

      const payload = {
        invoiceDate: invoiceDateIso,
        dueDate: dueDateIso || null,
        invoiceNumber,
        orderNumber: orderNumber || null,
        requisitionNumber: requisitionNumber || null,
        vesselId: selectedVesselId ? Number(selectedVesselId) : null,
        vesselName: vName,
        accountCode: distinctAccountCodes || null,
        customerId,
        customerName,
        salesperson: salesperson || null,
        subject: subject || null,
        customerNotes: customerNotes || null,
        termsConditions: termsConditions || null,
        status,
        subTotal,
        discountPercent: toNumber(discountPercent),
        discountAmount,
        taxType,
        taxName: selectedTax?.label || null,
        taxAmount: Math.abs(taxAmountRaw),
        adjustment: adjustmentAmount,
        filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
        total,
        balanceDue: finalTotal,
        lineItems: cleanedItems,
        appliedCreditNotes: appliedCredits.filter(c => c.appliedAmount > 0).map(c => ({
          id: c.id,
          appliedAmount: c.appliedAmount,
        })),
      };

      let targetId = invoiceId || null;
      if (isEditing && invoiceId) {
        await updateAccountInvoice(invoiceId, payload);
      } else {
        const created = await createAccountInvoice(payload);
        targetId = created?.id ?? created?.data?.id ?? null;
      }

      if (targetId) {
        await syncLineItems(targetId);
      }
      navigate(-1);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setItems((prev) => {
      const nextId = prev.reduce((max, row) => Math.max(max, row.id), 0) + 1;
      return [...prev, { id: nextId, description: '', accountCode: '', quantity: '1.00', rate: '0.00' }];
    });
  };

  const updateRow = (id, field, value) => {
    setItems(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeRow = (id) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(row => row.id !== id));
    }
  };

  const triggerDocumentSelect = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [] as File[]);
    if (!files.length) {
      return;
    }

    setUploadingFiles(true);
    setUploadError(null);

    try {
      const uploaded = await uploadMenuFiles('invoices', files);
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path;
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return { ...f, url: fileUrl };
      });
      setUploadedFiles((prev) => [...prev, ...formattedUploads]);
    } catch (err) {
      const message = 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('invoices', file.fileName);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path));
    } catch (err) {
      const message = 'Delete failed.';
      setUploadError(message);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">

              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">INVOICES</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">{isEditing ? 'Edit Invoice' : 'New Invoice'}</h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">{isEditing ? 'Update the invoice' : 'Create a new invoice'}</p>
                  {loadingInvoice && <p className="text-xs text-slate-400 mt-1">Loading invoice details...</p>}
                </div>
              </div>

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">

                {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <CustomerSelectorField
                        value={selectedCustomerId}
                        onChange={(id, name) => { setSelectedCustomerId(id); setSelectedCustomerName(name); setOrderNumber(''); }}
                        width="100%"
                        customersHook={customersHook}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Vessel */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Vessel</label>
                    </div>
                    <div className="flex-1 relative">
                      <SearchableSelect
                        value={selectedVesselId}
                        onChange={(e) => handleVesselChange(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">-- Choose Vessel --</option>
                        {vessels.map((v: any) => (
                          <option key={v.id} value={v.id}>
                            {v.fleetName || v.fleet_name || v.name || v.imoNumber || `Vessel #${v.id}`}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Invoice# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Invoice#</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1 relative">
                      <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Order Number */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Order Number</label>
                    </div>
                    <div className="flex-1 relative">
                      {selectedCustomerId ? (
                        <SearchableSelect 
                          value={orderNumber} 
                          onChange={(e) => handleSalesOrderSelect(e.target.value)} 
                          className={inputClass}
                        >
                          <option value="">-- Optional: Select a Sales Order --</option>
                          {allSalesOrders
                            .filter(o => String(o.customerId) === selectedCustomerId && o.status !== 'Draft')
                            .map(o => (
                              <option key={o.id} value={o.orderNumber}>
                                {o.orderNumber} (Total: ${Number(o.total).toFixed(2)})
                              </option>
                            ))
                          }
                          {orderNumber && !allSalesOrders.some(o => o.orderNumber === orderNumber && String(o.customerId) === selectedCustomerId && o.status !== 'Draft') && (
                            <option value={orderNumber}>{orderNumber}</option>
                          )}
                        </SearchableSelect>
                      ) : (
                        <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} className={inputClass} placeholder="Select a customer first to choose an order" disabled />
                      )}
                    </div>
                  </div>

                  {/* Requisition Number */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Requisition Number</label>
                    </div>
                    <div className="flex-1 relative">
                      <input type="text" value={requisitionNumber} onChange={(e) => setRequisitionNumber(e.target.value)} className={inputClass} placeholder="Enter Requisition Number" />
                    </div>
                  </div>

                  {/* Invoice Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Invoice Date</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Due Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Due Date</label>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment Terms</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={inputClass}>
                        <option value="Due on Receipt">Due on Receipt</option>
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Net 60">Net 60</option>
                        <option value="Due end of the month">Due end of the month</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Salesperson */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Salesperson</label>
                    </div>
                    <div className="flex-1">
                      <SalespersonField value={salesperson} onChange={setSalesperson} className={inputClass} />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Subject</label>
                    </div>
                    <div className="flex-1">
                      <textarea rows={2} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Let your customer know what this Invoice is for" className={`${inputClass} resize-none`}></textarea>
                    </div>
                  </div>
                </div>

                {/* Item Table */}
                <div className="mb-8 overflow-visible border border-slate-200 rounded-xl shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
                    <h3 className="text-sm font-bold text-slate-800">Item Table</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-175">
                      <thead>
                        <tr className="bg-white border-b border-slate-100">
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[36%]">Item Details</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[24%]">Account Code</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[12%]">Quantity</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[14%]">Rate <Calculator className="w-3 h-3 inline-block ml-1 opacity-50" /></th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[10%]">Amount</th>
                          <th className="px-4 py-3 w-[4%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((row) => (
                          <tr key={row.id} className="bg-white group hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-4 h-4 text-slate-300 mt-2 cursor-grab" />
                                <textarea
                                  rows={1}
                                  value={row.description}
                                  onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                                  placeholder="Type or click to select an item."
                                  className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all resize-none placeholder:text-slate-400"
                                ></textarea>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <SearchableSelect
                                value={row.accountCode || ''}
                                onChange={(e) => updateRow(row.id, 'accountCode', e.target.value)}
                                className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                                disabled={loadingAccountCodes}
                              >
                                <option value="">
                                  {loadingAccountCodes
                                    ? 'Loading...'
                                    : selectedVesselId
                                      ? 'Select account code'
                                      : 'Select vessel first'}
                                </option>
                                {availableAccountCodes.map((code) => (
                                  <option key={code} value={code}>
                                    {code}
                                  </option>
                                ))}
                                {row.accountCode && !availableAccountCodes.includes(row.accountCode) && (
                                  <option value={row.accountCode}>
                                    {row.accountCode}
                                  </option>
                                )}
                              </SearchableSelect>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, 'quantity', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.rate}
                                onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top text-right">
                              <div className="pt-2 text-sm font-bold text-slate-700">
                                {getRowAmount(row).toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top text-center">
                              {items.length > 1 && (
                                <button type="button" onClick={() => removeRow(row.id)} className="p-1.5 mt-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 rounded-b-xl">
                    <button type="button" onClick={addRow} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-100/50 hover:bg-blue-100 px-3 py-2 rounded-xl transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add New Row
                    </button>
                  </div>
                </div>

                {/* Additional Details & Totals */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <div className="lg:col-span-7 space-y-6">
                    <div>
                      <label className={labelClass}>Customer Notes</label>
                      <textarea
                        rows={3}
                        value={customerNotes}
                        onChange={(e) => setCustomerNotes(e.target.value)}
                        placeholder="Will be displayed on the invoice"
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Terms & Conditions</label>
                      <textarea
                        rows={3}
                        value={termsConditions}
                        onChange={(e) => setTermsConditions(e.target.value)}
                        placeholder="Enter the terms and conditions"
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Attach File(s) to Invoice</label>
                      <input ref={documentInputRef} type="file" multiple hidden onChange={handleDocumentChange} />
                      <button
                        type="button"
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 text-slate-400" /> {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">You can upload a maximum of 5 files, 10MB each</p>
                      {uploadError && <p className="text-[11px] text-red-500 mt-1 font-medium">{uploadError}</p>}
                      {uploadedFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {uploadedFiles.map((file) => (
                            <li key={`${file.fileName}-${file.path || ''}`} className="flex items-center gap-2 group">
                              <a href={file.url || file.path} target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteFile(file)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete file"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-slate-700">Sub Total</span>
                        <span className="text-sm font-bold text-slate-900">{subTotal.toFixed(2)}</span>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-500 w-24">Discount</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <div className="flex items-center w-24 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                            <input type="text" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} className="w-full text-right px-2 py-1.5 text-sm outline-none" />
                            <div className="px-2 py-1.5 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">%</div>
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{discountAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3 w-32">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TDS'} onChange={() => { setTaxType('TDS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TDS</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TCS'} onChange={() => { setTaxType('TCS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TCS</span>
                          </label>
                        </div>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <SearchableSelect value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-40 px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 transition-all">
                            {activeTaxOptions.length > 0 ? (
                              <>
                                <option value="">Select a Tax</option>
                                {activeTaxOptions.map(opt => (
                                  <option key={opt.label} value={String(opt.rate)}>{opt.label}</option>
                                ))}
                              </>
                            ) : (
                              <option value="" disabled>No results found</option>
                            )}
                          </SearchableSelect>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{taxAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-6">
                        <span className="text-sm font-semibold text-slate-500 w-24">Adjustment</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <input type="text" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="w-24 text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all" />
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{adjustmentAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                        <span className="text-lg font-black text-slate-900">Total ($)</span>
                        <span className="text-xl font-black text-blue-600">{total.toFixed(2)}</span>
                      </div>

                      {/* Credits Applied Section */}
                      {selectedCustomerId && customerCreditNotes.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-dashed border-slate-200">
                          <div className="flex items-center gap-2 mb-3">
                            <CreditCard className="w-4 h-4 text-emerald-600" />
                            <span className="text-[12px] font-bold text-emerald-700 uppercase tracking-wider">Apply Credit Notes</span>
                          </div>
                          <div className="space-y-2">
                            {customerCreditNotes.map((cn) => {
                              const isApplied = appliedCredits.some(a => a.id === cn.id);
                              const appliedEntry = appliedCredits.find(a => a.id === cn.id);
                              const cnBalance = Number(cn.balance) || 0;
                              const otherCreditsTotal = appliedCredits.filter(a => a.id !== cn.id).reduce((s, a) => s + (a.appliedAmount || 0), 0);
                              const remainingRoom = Math.max(0, total - otherCreditsTotal);
                              const canApply = remainingRoom > 0;
                              return (
                                <div key={cn.id} className={`p-3 rounded-xl border transition-all ${
                                  isApplied 
                                    ? 'bg-emerald-50 border-emerald-200' 
                                    : canApply ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-slate-50 border-slate-100 opacity-50'
                                }`}>
                                  <label className={`flex items-center gap-3 ${canApply || isApplied ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                    <input
                                      type="checkbox"
                                      checked={isApplied}
                                      disabled={!canApply && !isApplied}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          const maxApply = Math.min(cnBalance, remainingRoom);
                                          setAppliedCredits(prev => [...prev, {
                                            id: cn.id,
                                            creditNoteNumber: cn.creditNoteNumber || cn.credit_note_number || '-',
                                            availableBalance: cnBalance,
                                            appliedAmount: maxApply,
                                          }]);
                                        } else {
                                          setAppliedCredits(prev => prev.filter(a => a.id !== cn.id));
                                        }
                                      }}
                                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[12px] font-bold text-slate-800">{cn.creditNoteNumber || cn.credit_note_number || '-'}</div>
                                      <div className="text-[10px] text-slate-500">Balance: ${cnBalance.toFixed(2)}</div>
                                    </div>
                                    {isApplied && (
                                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                        <span className="text-[11px] font-semibold text-slate-500">$</span>
                                        <input
                                          type="text"
                                          value={appliedEntry?.appliedAmount ?? ''}
                                          onChange={(e) => {
                                            const maxAllowed = Math.min(cnBalance, remainingRoom);
                                            const val = Math.min(Math.max(Number(e.target.value) || 0, 0), maxAllowed);
                                            setAppliedCredits(prev => prev.map(a =>
                                              a.id === cn.id ? { ...a, appliedAmount: val } : a
                                            ));
                                          }}
                                          className="w-20 text-right px-2 py-1 bg-white border border-emerald-300 rounded-lg text-sm font-bold text-emerald-700 outline-none focus:ring-2 focus:ring-emerald-100"
                                        />
                                      </div>
                                    )}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                          {totalCreditsApplied > 0 && (
                            <div className="mt-3 flex items-center justify-between">
                              <span className="text-sm font-bold text-emerald-700">Credits Applied</span>
                              <span className="text-sm font-black text-emerald-600">-{totalCreditsApplied.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                            <span className="text-lg font-black text-slate-900">Balance Due ($)</span>
                            <span className="text-xl font-black text-blue-600">{finalTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  {(!isEditing || invoiceStatus === 'Draft') ? (
                    <>
                      <button type="button" onClick={() => handleSave('Draft')} disabled={saving} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50 disabled:opacity-50">
                        {isEditing ? 'Update Draft' : 'Save as Draft'}
                      </button>
                      <button type="button" onClick={() => handleSave('Submitted')} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                        {isEditing ? 'Save and Submit' : 'Save and Submit'}
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => handleSave(invoiceStatus)} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                      {saving ? 'Saving...' : 'Update Invoice'}
                    </button>
                  )}
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

